<template>
  <div class="gallery-wrapper contained" ref="galleryComponent" :class="{ 'overlay-active': overLayActive }">

    <div v-if="options.ShowFilteringFacets" class="gallery-filters">
      <div class="gallery-filter-heading">{{options.CategoryHeadingLabel}}:</div>
      <accessible-tabs>
        <ul role="list" :aria-label="options.CategoryHeadingLabel">
          <li
            role="listitem"
            :data-name="options.CategoryAllLabel"
            :class="{ 'is-active': !currentCategory }"
            :aria-selected="!currentCategory"
            :tabindex="!currentCategory ? '0' : '-1'"
            @click="$router.push({ query: {} })"
            @keypress.enter.prevent="$router.push({ query: {} })"
            @keypress.space.prevent="$router.push({ query: {} })"
          >
            {{ options.CategoryAllLabel }}
          </li>
          <li
            v-for="category in categories"
            :key="category.Id"
            :id="category.Id"
            role="tab"
            :data-name="category.Description"
            :class="{ 'is-active': currentCategory === category.Description }"
            :aria-selected="currentCategory === category.Description"
            :tabindex="currentCategory === category.Description ? '0' : '-1'"
            @click="$router.push({ query: { category: category.Description } })"
            @keypress.enter.prevent="$router.push({ query: { category: category.Description } })"
            @keypress.space.prevent="$router.push({ query: { category: category.Description } })"
          >
            {{ category.Description }}
          </li>
        </ul>
      </accessible-tabs>
    </div>

    <div class="gallery-filter-view">
      <div class="gallery-filter-view-label">{{options.MobileViewLabel}}</div>
      <div class="gallery-filter-view-icons">
        <button
          class="stack-view"
          aria-label=""
          :class="{ 'active': !IsMobileGridView }"
          :disabled="!IsMobileGridView"
          @click="toggleGridView('stack')"
          @keypress.enter.prevent="toggleGridView('stack')"
          @keypress.space.prevent="toggleGridView('stack')"
        ></button>
        <button
          class="grid-view"
          aria-label=""
          :class="{ 'active': IsMobileGridView }"
          :disabled="IsMobileGridView"
          @click="toggleGridView('grid')"
          @keypress.enter.prevent="toggleGridView('stack')"
          @keypress.space.prevent="toggleGridView('stack')"
        ></button>
      </div>
    </div>

    <transition-group
      tag="div"
      name="gallery-grid"
      class="gallery-grid"
      ref="galleryGrid"
      :class="{
        'stack': !IsMobileGridView && isMobile,
        'carousel-opened': overLayActive
      }"
    >

      <div v-for="(item, index) in items"
        :key="item.Id + 'gallery'"
        class="tile"
        tabindex="0"
        :id="item.Id"
        :class="{
          'video': item.IsVideo === true,
          'featured-1': item.Enhance === true && index === 0,
          'featured-2': item.Enhance === true && index === 1,
          'enhanced': item.Enhance === true && index > 1
        }"
        @click.prevent="setCurrentImage(index)"
        @keypress.enter.prevent="setCurrentImage(index)"
        @keypress.space.prevent="setCurrentImage(index)"
      >
        <!-- <div class="loading-overlay" :class="{ 'loading': loadingImages}"/> -->
        <div class="content" :class="[{ 'grid-view': IsMobileGridView && isMobile }]">
          <div class="tile-media" :data-id="item.Id" :id="`position-${index}`">
            <div v-if="(item.IsVideo && IsMobileGridView) || (item.IsVideo && !isMobile)" class="gallery-play-icon">
              <span>{{options.Play}}</span>
            </div>
            <picture
              class="image-wrapper"
              :class="[{ 'stack': !IsMobileGridView && isMobile },{ 'video': item.IsVideo }]"
              v-lazyload="{
                src: item.IsVideo ? renderGridMedia(item.DesktopVideoPosterImage) : item.ImageSrc,
                set: [{ width: '800', breakpoint: 'xs' }],
                fit: 'crop',
                align: 'center',
                ratio:  item.ImageRatio,
                updateCallback: imageLoaded
              }"
            >
              <img
                class="image"
                src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                :alt="item.Title"
              />
            </picture>
          </div>
          <video-player
            v-if="item.IsVideo && !IsMobileGridView && isMobile"
            :class="{ 'stack': !IsMobileGridView && isMobile }"
            :desktop-video="item.DesktopVideo"
            :desktop-video-poster-image="item.DesktopVideoPosterImage !== '' ? item.DesktopVideoPosterImage : options.GalleryMissingImage"
            :disabled-toggle="!IsMobileGridView ? false : true"
            :pause-control="currentPlaying !== item.Id"
            :dictionary="videoPlayerDictionary"
            :alt-text="item.Title"
            pause-on-hide
            @mounted="imageLoaded"
          />
          <div v-show="!IsMobileGridView && isMobile" class="tile-grid-content">
            <div class="gallery-item-title">{{item.Title}}</div>
            <div v-if="item.ShortDescription.length" class="gallery-item-description">
              {{item.ShortDescription}}
            </div>
            <div v-if="item.CtaUrl.length" class="gallery-item-cta">
              <a
                @click.prevent.stop="handleAnchorClick(item)"
                :href="item.CtaUrl"
                :target="item.CtaTarget"
                v-html="item.CtaText != '' ? item.CtaText : options.LearnMore"
              />
            </div>
          </div>
        </div>
      </div>

    </transition-group>

    <div v-if="!allLoaded" class="gallery-load-more show">
      <button
        @click="loadMore()"
        @keypress.enter.prevent="loadMore()"
        @keypress.space.prevent="loadMore()"
      >{{options.LoadMore}}</button>
    </div>

    <div
      v-if="overLayActive"
      class="gallery-overlay"
      :class="{ 'show': overLayActive }"
    >
      <div class="gallery-overlay-background"></div>
      <div class="gallery-overlay-content contained">

        <gallery-carousel
          :items="filteredItems"
          :options="options"
          @close="unSetCurrentImage"
          @scroll="scrollToImage"
        />

      </div>
    </div>
  </div>
</template>
 <script>

import { mapState } from 'vuex'
import ListMixin from '@mixins/list-mixin'
import GalleryCarousel from './gallery-carousel.vue'
import AccessibleTabs from '../../01.base/accessible-tabs/accessible-tabs'

export default {
  name: 'gallery-list',
  components: {
    GalleryCarousel,
    AccessibleTabs
  },
  mixins: [ListMixin],
  props:{
    categories: {
      type: Array,
      required: true
    },
    options: {
      type: Object,
      required: true
    },
    limitOverwrite: {
      type: Number
    }
  },
  data(){
    return {
      loadedImages: 0,
      loadingImages: true,
      initialLoad: false,
      currentPlaying: null,
      currentMediaVideoHeight: '300',
      IsMobileGridView: true //setting to true always. removing from sitecore CMS
    }
  },
  inject: ['videoPlayerDictionary'],
  computed: {
    ...mapState('responsive', ['breakpoints', 'width']),
    ...mapState('global', ['isSitecore']),
    isMobile() {
      return this.width <= 767
    },
    currentCategory() {
      return this.$route.query.category
    },
    currentImageIndex() {
      return Number(this.$route.query.imageIndex)
    },
    currentImage() {
      return this.items[this.currentImageIndex - 1]
    },
    overLayActive() {
      return this.currentImageIndex && !this.isMobile
    }
  },
  watch: {
    items: {
      handler() {
        this.loadingImages = true
        this.$nextTick(() => {
          this.resizeAllGridItems()
        })
      },
      deep: true
    },
    isMounted(val) {
      if (val) {
        this.initGallery()
      }
    },
    currentCategory(val) {
      this.applyCategory(val)
    },
    overLayActive(val) {
      if (val) {
        document.body.classList.add('no-scroll')
      } else {
        document.body.classList.remove('no-scroll')
      }
    },
    currentImageIndex(val) {
      if (this.isMobile) {
        if (val) {
          this.scrollToImage()
        } else {
          this.IsMobileGridView = true
        }
      }
    },
    loadedImages(val) {
      if (val >= this.items.length) {
        this.resizeAllGridItems()
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.currentImageIndex > this.pagination.$limit) {
        this.pagination.$limit = this.currentImageIndex
      }
    })
  },
  updated() {
    this.resizeAllGridItems()
  },
  methods: {
    initGallery() {
      //if (this.isSitecore) { return } //Don't let the remaining code run in Sitece CMS EE
      this.applyCategory()
      window.addEventListener("resize", this.resizeAllGridItems);
    },
    applyCategory() {
      if (this.$route.query.category) {
        this.updateFilter({ 'Categories.Description': this.$route.query.category })
      } else if (!this.$route.query.category && Object.keys(this.filters).length > 0) {
        this.updateFilter({})
      }
    },
    setCurrentImage(index) {
      //document.body.classList.add('no-scroll')
      if (!this.isMobile || this.IsMobileGridView) {
        this.$router.push({
          query: {
            ...this.$route.query,
            imageIndex: index + 1
          }
        })
      }
    },
    unSetCurrentImage() {
      const query = {}
      if (this.currentCategory) {
        query.category = this.currentCategory
      }
      this.$router.push({ query })
    },
    renderGridMedia(el) {
      return el !== '' ? el : this.options.GalleryMissingImage
    },
    resizeGridItem(item){
      return new Promise((resolve) => {
        this.$nextTick(() => {
          item.classList.remove('resized')
          let grid = this.$refs.galleryGrid.$el
          let rowHeight = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-auto-rows'))
          let rowGap = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-row-gap'))
          const itemHeight = item.querySelector('.content').getBoundingClientRect().height
          let rowSpan = Math.ceil((itemHeight+rowGap)/(rowHeight+rowGap))
          item.style.gridRowEnd = "span " + rowSpan
          item.classList.add('resized')
          return resolve()
        })
      })
    },
    resizeAllGridItems(){
      this.$nextTick(async () => {
        this.loadingImages = true
        const allItems = this.$el.getElementsByClassName("tile")

        for (const item of allItems) {
          await this.resizeGridItem(item)
        }
        this.loadingImages = false
        if (this.isMobile && this.currentImage && !this.initialLoad) {
          this.initialLoad = true
          this.$nextTick(() => {
            this.scrollToImage()
          })
        }
      })
    },
    scrollToImage(scrollData) {
      const currentImage = scrollData ? this.items[scrollData.imageIndex - 1] : this.currentImage
      this.IsMobileGridView = false
      this.$nextTick(() => {
        const element = this.$refs.galleryGrid.$el.querySelector('[id="' + currentImage.Id + '"]')
        if (element) {
          element.scrollIntoView({
            behavior: scrollData ? scrollData.behavior : 'smooth',
            block: 'center'
          })
        }
      })
    },
    loadMore() {
      this.pagination.$limit = this.pagination.$limit + (this.limitOverwrite || this.defaultLimit)
    },
    toggleGridView(view) {
      this.resizeAllGridItems();
      if (view == 'stack') {
        this.setCurrentImage(0)
      } else {
        this.unSetCurrentImage()
      }
    },
    imageLoaded() {
      this.loadedImages++
    },
    videoPlay(itemId) {
      this.currentPlaying = itemId
    },
    videoPause(itemId) {
      if (this.currentPlaying === itemId) {
        this.currentPlaying = null
      }
    },
    handleAnchorClick(item) {
      window.open(item.CtaUrl, item.CtaTarget)
    }
  }
};
</script>
